import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Form, Button, Spinner, Card } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useShiftReportStore } from '../../state/store/shiftReportStore';
import { format, parse } from 'date-fns';
import './ShiftReport.css';
import { useSelector, useDispatch } from 'react-redux';
import { State } from '../../state';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faFilePdf, faCalendarAlt, faSearch, faShare } from '@fortawesome/free-solid-svg-icons';
import '@fontsource/roboto-mono';
import { useHistory, useLocation } from 'react-router-dom';
import { setGlobalAlert } from '../../state/action-creators/alert';

const ShiftReport: React.FC = () => {
  const { 
    reports, 
    loading, 
    error, 
    fetchReports, 
    setSelectedDate,
    setSelectedShiftIds
  } = useShiftReportStore();
  
  const [selectedDate, setDate] = useState<Date | null>(null);
  const [shiftIdInput, setShiftIdInput] = useState<string>("");
  const language = useSelector((state: State) => state.languageReducer);
  const lang = language?.data?.shiftReport || {};
  const reportRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  
  // Add location and navigation hooks
  const location = useLocation();
  const history = useHistory();
  
  // Parse URL query parameters
  const getQueryParams = () => {
    const searchParams = new URLSearchParams(location.search);
    return {
      date: searchParams.get('date'),
      shiftIds: searchParams.get('shiftIds') || searchParams.get('shiftId') || null
    };
  };
  
  // Update URL with current search parameters
  const updateUrlParams = (date: Date | null, shiftIds: string) => {
    const searchParams = new URLSearchParams();
    const params = getQueryParams();
    const paramName = location.search.includes('shiftId=') ? 'shiftId' : 'shiftIds';
    
    // Only add date parameter if it's explicitly provided
    if (date) {
      searchParams.set('date', format(date, 'yyyy-MM-dd'));
    }
    
    if (shiftIds && shiftIds.trim() !== '') {
      searchParams.set(paramName, shiftIds);
    }
    
    const newUrl = `${location.pathname}?${searchParams.toString()}`;
    history.replace(newUrl);
  };
  
  // Function to copy current URL to clipboard
  const copyLinkToClipboard = () => {
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl)
      .then(() => {
        // Show popup notification instead of alert
        dispatch(setGlobalAlert(
          lang.linkCopied || 'Link copied to clipboard!',
          'success'
        ));
      })
      .catch(err => {
        console.error('Failed to copy link: ', err);
        dispatch(setGlobalAlert(
          'Failed to copy link to clipboard',
          'error'
        ));
      });
  };
  
  useEffect(() => {
    // Parse URL parameters on initial load
    const params = getQueryParams();
    let initialDate: Date | null = null;
    let initialShiftIds = "";
    
    // Set date from URL if provided
    if (params.date) {
      try {
        initialDate = parse(params.date, 'yyyy-MM-dd', new Date());
        setDate(initialDate);
      } catch (error) {
        console.error('Invalid date format in URL', error);
      }
    }
    
    // Set shift IDs from URL if provided
    if (params.shiftIds) {
      initialShiftIds = params.shiftIds;
      setShiftIdInput(initialShiftIds);
      
      const parsedShiftIds = parseShiftIds(initialShiftIds);
      if (parsedShiftIds.length > 0) {
        setSelectedShiftIds(parsedShiftIds);
        
        // If we have valid shift IDs from URL, fetch reports with those IDs
        fetchReports(
          initialDate ? format(initialDate, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX") : undefined, 
          parsedShiftIds
        );
        return; // Exit early to avoid double fetching
      }
    }
    
    // Handle date-only case if no shift IDs were processed
    if (initialDate) {
      const formattedDate = format(initialDate, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");
      setSelectedDate(formattedDate);
      fetchReports(formattedDate);
    }
    // Do not fetch reports if neither date nor shift IDs are provided
  }, [location.search]); // Re-run when URL search parameters change
  
  const handleDateChange = (date: Date | null) => {
    setDate(date);
    if (date) {
      handleSearch(date, shiftIdInput);
    }
  };

  const handleShiftIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShiftIdInput(e.target.value);
  };
  
  // Parse multiple shift IDs from input (comma or space separated)
  const parseShiftIds = (input: string): number[] => {
    if (!input || !input.trim()) return [];
    
    // Split by comma or space
    return input.split(/[\s,;]+/)
      .filter(id => id.trim() !== '') // Remove empty strings
      .map(id => parseInt(id.trim(), 10)) // Convert to integers
      .filter(id => !isNaN(id)); // Filter out invalid numbers
  };
  
  const handleSearch = (date: Date | null = selectedDate, shiftIdValue: string = shiftIdInput) => {
    const shiftIds = parseShiftIds(shiftIdValue);
    setSelectedShiftIds(shiftIds);
    
    // Update URL with search parameters
    updateUrlParams(date, shiftIdValue);
    
    if (shiftIds.length > 0) {
      // If shift IDs are provided
      fetchReports(date ? format(date, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX") : undefined, shiftIds);
    } else if (date) {
      // If date is provided
      const formattedDate = format(date, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");
      setSelectedDate(formattedDate);
      fetchReports(formattedDate);
    }
    // Do not fetch reports if neither date nor shift IDs are provided
  };
  
  const formatCurrency = (amount: number | null): string => {
    if (amount === null || amount === undefined) return formatCurrency(0);
    
    return new Intl.NumberFormat(language?.appLanguage === 'tr' ? 'tr-TR' : 'en-US', { 
      style: 'currency', 
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Calculate deficit (cashAmount + creditCardAmount - totalSale)
  const calculateDeficit = (totalSale: number, cashAmount: number, creditCardAmount: number | null) => {
    const ccAmount = creditCardAmount || 0;
    return (cashAmount + ccAmount) - totalSale;
  };

  // Function to export reports data to Excel
  const exportToExcel = () => {
    if (!reports || reports.length === 0) return;
    
    // Create worksheet data
    const worksheetData: any[] = [];
    
    reports.forEach(report => {
      // Add shift header
      worksheetData.push([`${lang.shift || 'Shift'} ${report.shiftId}`, '', '', '', '', '']);
      worksheetData.push([`${lang.totalSale || 'Total Sale'}: ${formatCurrency(report.totalSale)}`, '', '', '', '', '']);
      worksheetData.push(['', '', '', '', '', '']);
      
      // Add employees header
      worksheetData.push([lang.employees?.title || 'Employees']);
      worksheetData.push([
        lang.employees?.name || 'Personnel',
        lang.employees?.totalSale || 'Total Sale',
        lang.employees?.cashAmount || 'Cash Amount',
        lang.employees?.creditCardAmount || 'Credit Card Amount',
        lang.employees?.difference || 'Difference',
        lang.employees?.note || 'Note'
      ]);
      
      // Add employee rows
      if (report.employees && report.employees.length > 0) {
        report.employees.forEach(employee => {
          worksheetData.push([
            employee.employeeName,
            employee.totalSale,
            employee.cashAmount,
            employee.creditCardAmount || 0,
            calculateDeficit(employee.totalSale, employee.cashAmount, employee.creditCardAmount),
            employee.note || ''
          ]);
        });
      } else {
        worksheetData.push([lang.noEmployeeData || 'No employee data available', '', '', '', '', '']);
      }
      
      // Add employee totals
      worksheetData.push([
        lang.employees?.totals || 'Totals',
        report.totalSale,
        report.cashAmount,
        report.employees?.reduce((sum, emp) => sum + (emp.creditCardAmount || 0), 0) || 0,
        calculateDeficit(
          report.totalSale,
          report.cashAmount,
          report.employees?.reduce((sum, emp) => sum + (emp.creditCardAmount || 0), 0) || 0
        ),
        ''
      ]);
      
      // Add space between sections
      worksheetData.push(['', '', '', '', '', '']);
      
      // Add credit card batches header
      worksheetData.push([lang.creditCardBatches?.title || 'Credit Card Batches']);
      worksheetData.push([
        lang.creditCardBatches?.bank || 'Bank',
        lang.creditCardBatches?.batchNo || 'Batch No',
        lang.creditCardBatches?.amount || 'Amount',
        '', '', ''
      ]);
      
      // Add credit card batch rows
      if (report.creditCardBatches && report.creditCardBatches.length > 0) {
        report.creditCardBatches.forEach(batch => {
          worksheetData.push([
            batch.bankName,
            batch.batchNo || '-',
            batch.amount,
            '', '', ''
          ]);
        });
        
        // Add batch totals
        worksheetData.push([
          lang.creditCardBatches?.total || 'Total',
          '',
          report.creditCardBatches.reduce((sum, batch) => sum + (batch.amount || 0), 0),
          '', '', ''
        ]);
      } else {
        worksheetData.push([lang.noBatchData || 'No credit card batch data available', '', '', '', '', '']);
      }
      
      // Add space between reports
      worksheetData.push(['', '', '', '', '', '']);
      worksheetData.push(['', '', '', '', '', '']);
    });
    
    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // Set column widths
    const colWidths = [
      { wch: 20 }, // Personnel
      { wch: 15 }, // Total Sale
      { wch: 15 }, // Cash Amount
      { wch: 20 }, // Credit Card Amount
      { wch: 15 }, // Difference
      { wch: 25 }, // Note
    ];
    worksheet['!cols'] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Shift Report');
    
    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Save the file
    const fileName = `ShiftReport_${selectedDate ? format(selectedDate, 'yyyy-MM-dd') : 'All'}.xlsx`;
    saveAs(data, fileName);
  };

  // Function to export reports to PDF
  const exportToPDF = async () => {
    if (!reportRef.current || !reports || reports.length === 0) return;
    
    try {
      const content = reportRef.current;
      const canvas = await html2canvas(content, {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        logging: false,
        backgroundColor: 'white' // Match the background color
      });
      
      // Create PDF with A4 dimensions (landscape if needed for wider tables)
      const orientation = canvas.width > canvas.height * 1.2 ? 'l' : 'p';
      const pdf = new jsPDF({
        orientation: orientation,
        unit: 'mm',
        format: 'a4'
      });
      
      // Get PDF dimensions in mm
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate the margins - use small margins to maximize space
      const margin = 5; // 5mm margin
      const availableWidth = pdfWidth - 2 * margin;
      const availableHeight = pdfHeight - 2 * margin;
      
      // Calculate scale to fit width while maintaining aspect ratio
      const scale = availableWidth / canvas.width;
      const scaledHeight = canvas.height * scale;
      
      // Determine if we need multiple pages
      if (scaledHeight <= availableHeight) {
        // Content fits on a single page
        // Center vertically if there's extra space
        const y = margin + (availableHeight - scaledHeight) / 2;
        
        pdf.addImage(
          canvas.toDataURL('image/png'), 
          'PNG', 
          margin, 
          y, 
          availableWidth, 
          scaledHeight
        );
      } else {
        // Content requires multiple pages
        // Calculate how many pages we need
        const pageCount = Math.ceil(scaledHeight / availableHeight);
        
        for (let i = 0; i < pageCount; i++) {
          if (i > 0) {
            pdf.addPage();
          }
          
          // Calculate the portion of the canvas to use for this page
          const sourceY = (i * availableHeight / scale);
          
          // For the last page, we might not need the full height
          const remainingHeight = canvas.height - sourceY;
          const pageSourceHeight = Math.min(availableHeight / scale, remainingHeight);
          const pageDestHeight = pageSourceHeight * scale;
          
          // Create a temporary canvas for the current portion
          const tempCanvas = document.createElement('canvas');
          const tempCtx = tempCanvas.getContext('2d');
          tempCanvas.width = canvas.width;
          tempCanvas.height = pageSourceHeight;
          
          if (tempCtx) {
            // Draw the portion of the original canvas to the temporary canvas
            tempCtx.drawImage(
              canvas,
              0, sourceY, canvas.width, pageSourceHeight,
              0, 0, canvas.width, pageSourceHeight
            );
            
            // Add this portion to the PDF
            pdf.addImage(
              tempCanvas.toDataURL('image/png'),
              'PNG',
              margin,
              margin,
              availableWidth,
              pageDestHeight
            );
          }
        }
      }
      
      // Save the PDF
      const fileName = `ShiftReport_${selectedDate ? format(selectedDate, 'yyyy-MM-dd') : 'All'}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };
  
  // Get total of all shifts
  const getGrandTotal = () => {
    if (!reports || reports.length === 0) return { 
      totalSale: 0,
      cashAmount: 0,
      creditCardAmount: 0,
      deficit: 0
    };
    
    const grandTotal = reports.reduce((acc, report) => {
      const reportCreditCardAmount = report.employees?.reduce(
        (sum, emp) => sum + (emp.creditCardAmount || 0), 0
      ) || 0;
      
      return {
        totalSale: acc.totalSale + report.totalSale,
        cashAmount: acc.cashAmount + report.cashAmount,
        creditCardAmount: acc.creditCardAmount + reportCreditCardAmount,
        deficit: acc.deficit + calculateDeficit(report.totalSale, report.cashAmount, reportCreditCardAmount)
      };
    }, { totalSale: 0, cashAmount: 0, creditCardAmount: 0, deficit: 0 });
    
    return grandTotal;
  };

  // Helper to apply the appropriate class for deficit values
  const getDeficitClassName = (value: number): string => {
    return value >= 0 ? 'currency-value positive' : 'currency-value negative';
  };

  // Render the new table-based layout
  const renderTableView = () => {
    const grandTotal = getGrandTotal();
    
    return (
      <div ref={reportRef} className="report-container">
        <table className="shift-report-table">
          <colgroup>
            <col style={{ width: '16%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '14%' }} />
          </colgroup>
          <thead>
            <tr>
              <th colSpan={7} className="shift-header">
                {lang.title || 'Daily Shift Report'}
                {selectedDate ? ` - ${format(selectedDate, 'dd/MM/yyyy')}` : ''}
                {shiftIdInput ? ` - ${lang.shiftId || 'Shift ID'}: ${shiftIdInput}` : ''}
              </th>
            </tr>
            <tr>
              <th>{lang.employees?.name || 'Personnel'}</th>
              <th>{lang.employees?.totalSale || 'Total Sale'}</th>
              <th>{lang.employees?.cashAmount || 'Cash Amount'}</th>
              <th>{lang.employees?.creditCardAmount || 'Credit Card Amount'}</th>
              <th>{lang.creditCardBatches?.title || 'Credit Cards Total'}</th>
              <th>{lang.employees?.note || 'Note'}</th>
              <th>{lang.employees?.difference || 'Difference'}</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report, reportIndex) => (
              <React.Fragment key={report.id || reportIndex}>
                {/* Shift header row */}
                <tr>
                  <td className="shift-number">({reportIndex + 1})</td>
                  <td colSpan={6}>{lang.shift || 'Shift'} {report.shiftId}</td>
                </tr>
                
                {/* Employee rows */}
                {report.employees && report.employees.length > 0 ? (
                  report.employees.map((employee, index) => {
                    const deficit = calculateDeficit(
                      employee.totalSale,
                      employee.cashAmount,
                      employee.creditCardAmount
                    );
                    
                    return (
                      <tr key={index} className="employee-row">
                        <td>{employee.employeeName}</td>
                        <td className="currency-value">{formatCurrency(employee.totalSale)}</td>
                        <td className="currency-value">{formatCurrency(employee.cashAmount)}</td>
                        <td className="currency-value">{formatCurrency(employee.creditCardAmount)}</td>
                        <td className="currency-value"></td>
                        <td>{employee.note || '-'}</td>
                        <td className={getDeficitClassName(deficit)}>
                          {formatCurrency(deficit)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center">
                      {lang.noEmployeeData || 'No employee data available'}
                    </td>
                  </tr>
                )}
                
                {/* Shift total row */}
                <tr className="total-row">
                  <td>{lang.employees?.totals || 'Totals'}</td>
                  <td className="currency-value">{formatCurrency(report.totalSale)}</td>
                  <td className="currency-value">{formatCurrency(report.cashAmount)}</td>
                  <td className="currency-value">{formatCurrency(
                    report.employees?.reduce((sum, emp) => sum + (emp.creditCardAmount || 0), 0) || 0
                  )}</td>
                  <td className="currency-value"></td>
                  <td></td>
                  <td className={getDeficitClassName(
                    calculateDeficit(
                      report.totalSale,
                      report.cashAmount,
                      report.employees?.reduce((sum, emp) => sum + (emp.creditCardAmount || 0), 0) || 0
                    )
                  )}>
                    {formatCurrency(
                      calculateDeficit(
                        report.totalSale,
                        report.cashAmount,
                        report.employees?.reduce((sum, emp) => sum + (emp.creditCardAmount || 0), 0) || 0
                      )
                    )}
                  </td>
                </tr>
                
                {/* Credit card batches */}
                {report.creditCardBatches && report.creditCardBatches.length > 0 && (
                  <>
                    {report.creditCardBatches.map((batch, index) => (
                      <tr key={`batch-${index}`}>
                        <td className="batch-label">{batch.bankName}</td>
                        <td className="batch-number" colSpan={1}>
                          {lang.creditCardBatches?.batchNo || 'BATCH no'}: {batch.batchNo || '-'}
                        </td>
                        <td className="currency-value" colSpan={1}>{formatCurrency(batch.amount)}</td>
                        <td colSpan={4}></td>
                      </tr>
                    ))}
                  </>
                )}
                
                {/* Spacing row between shifts */}
                <tr>
                  <td colSpan={7} style={{ height: '20px' }}></td>
                </tr>
              </React.Fragment>
            ))}
            
            {/* Grand total row */}
            <tr className="grand-total-row">
              <td>{lang.grandTotal || 'Grand Total'}</td>
              <td className="currency-value">{formatCurrency(grandTotal.totalSale)}</td>
              <td className="currency-value">{formatCurrency(grandTotal.cashAmount)}</td>
              <td className="currency-value">{formatCurrency(grandTotal.creditCardAmount)}</td>
              <td className="currency-value">{formatCurrency(grandTotal.creditCardAmount)}</td>
              <td></td>
              <td className={getDeficitClassName(grandTotal.deficit)}>
                {formatCurrency(grandTotal.deficit)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Container fluid className="mt-5 mb-5">
      <h1 className="mb-5 text-center" style={{ color: 'var(--primary-dark)', fontWeight: 600 }}>
        {lang.title || 'Daily Shift Report'}
      </h1>
      
      <Row className="mb-4 justify-content-center">
        <Col md={10} lg={8} xl={6}>
          <Card>
            <Card.Body className="p-4">
              <div className="search-grid">
                <div>
                  <Form.Label className="mb-2 fw-bold" style={{ color: 'var(--primary-dark)' }}>
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                    {lang.selectDate || 'Select Date'}
                  </Form.Label>
                  <div className="d-flex">
                    <DatePicker
                      selected={selectedDate}
                      onChange={handleDateChange}
                      className="form-control"
                      dateFormat="dd/MM/yyyy"
                      popperClassName="datepicker-popper"
                      popperPlacement="bottom-start"
                      wrapperClassName="date-picker-wrapper"
                      portalId="datepicker-portal-root"
                      isClearable
                    />
                    {selectedDate && (
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        className="ms-2"
                        onClick={() => {
                          setDate(null);
                          handleSearch(null, shiftIdInput);
                        }}
                        title={lang.clearDateFilter || 'Clear date filter'}
                      >
                        ×
                      </Button>
                    )}
                  </div>
                </div>
                <div>
                  <Form.Label className="mb-2 fw-bold" style={{ color: 'var(--primary-dark)' }}>
                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                    {lang.shiftId || 'Shift ID'}
                  </Form.Label>
                  <div className="input-with-button">
                    <Form.Control
                      type="text"
                      placeholder={lang.enterShiftIds || 'e.g. 2025031101, 2025031102'}
                      value={shiftIdInput}
                      onChange={handleShiftIdChange}
                    />
                    <Button 
                      onClick={() => handleSearch()}
                      className="search-button"
                    >
                      {lang.search || 'Search'}
                    </Button>
                  </div>
                  <Form.Text className="text-muted mt-2">
                    {lang.multipleShiftIds || 'Enter multiple IDs separated by comma'}
                  </Form.Text>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {!loading && reports.length > 0 && (
        <Row className="mb-4 justify-content-center">
          <Col md={10} lg={8} xl={6}>
            <div className="export-buttons">
              <Button 
                onClick={copyLinkToClipboard}
                className="export-button share"
                style={{ backgroundColor: 'var(--primary-color)' }}
              >
                <FontAwesomeIcon icon={faShare} />
                {lang.shareLink || 'Share Link'}
              </Button>
              <Button 
                onClick={exportToExcel}
                className="export-button excel"
              >
                <FontAwesomeIcon icon={faFileExcel} />
                {lang.exportExcel || 'Export to Excel'}
              </Button>
              <Button 
                onClick={exportToPDF}
                className="export-button pdf"
              >
                <FontAwesomeIcon icon={faFilePdf} />
                {lang.exportPDF || 'Export to PDF'}
              </Button>
            </div>
          </Col>
        </Row>
      )}
      
      {loading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" style={{ color: 'var(--primary-color)' }} role="status">
            <span className="visually-hidden">{lang.loading || 'Loading...'}</span>
          </Spinner>
        </div>
      ) : error ? (
        <div className="alert alert-danger" role="alert">
          {lang.error || error}
        </div>
      ) : reports.length === 0 ? (
        <div className="alert alert-info" role="alert" style={{ backgroundColor: 'var(--primary-light)', color: 'var(--primary-dark)', border: 'none', borderRadius: 'var(--border-radius)' }}>
          {lang.noReports || 'No shift reports found for the selected date.'}
        </div>
      ) : (
        renderTableView()
      )}
    </Container>
  );
};

export default ShiftReport; 